import { useState, useCallback } from 'react';

interface UseApiCallReturn<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  execute: (...args: any[]) => Promise<T>;
  reset: () => void;
}

/**
 * Custom hook for handling API calls with standardized loading and error states
 * @param apiFunction The API function to call
 * @returns Object containing data, loading state, error state, and execute function
 */
export function useApiCall<T>(
  apiFunction: (...args: any[]) => Promise<T>
): UseApiCallReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(async (...args: any[]) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await apiFunction(...args);
      setData(result);
      return result;
    } catch (err) {
      console.error('API call failed:', err);
      const errorMsg = err instanceof Error 
        ? `${err.message}. Please try again later or contact support.` 
        : 'Operation failed. Please try again later.';
      setError(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [apiFunction]);

  const reset = useCallback(() => {
    setData(null);
    setIsLoading(false);
    setError(null);
  }, []);

  return { data, isLoading, error, execute, reset };
}

export default useApiCall; 